import styled, { css } from 'styled-components';
import { IconContainer } from '../../../../icon';
import Typography from '../../../../typography';
import { pxToRem } from '../../../../../design-tokens/style-utilities';
import { pickToken } from '../../../styles/helper';
import { isValidImageUrl } from '../helpers';
import { RadioCheckboxInputContainer, baseStateStyles, overrideCoreUIStyles, stateStyles, } from './common.styled';
const CHECKBOX_BOX_SIZE = 24;
const CHECKBOX_HITBOX_OFFSET_HORIZONTAL = 6;
const CHECKBOX_HITBOX_OFFSET_VERTICAL = 10;
const CHECKBOX_CONTENT_SIZE = 56;
const CHECKBOX_HORIZONTAL_IMAGE_WIDTH = 70;
const CHECKBOX_VERTICAL_IMAGE_WIDTH = 56;
/**
 * Checkbox Basic
 */
export const checkboxBasicStyles = ({ theme, $colorContext, }) => css `
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: ${pxToRem(CHECKBOX_BOX_SIZE)};
	height: ${pxToRem(CHECKBOX_BOX_SIZE)};
	background-color: ${theme.colors.blanc.bgFormField};
	border: ${pxToRem(theme.borderWidth[2])} solid
		${pickToken({
    'on-light': theme.colors.medium.borderFormField,
    'on-medium': theme.colors.medium.borderFormField,
    'on-dark': theme.colors.blanc.bgFormField,
}, $colorContext)};
	border-radius: ${pxToRem(theme.borderRadius.s)};
	color: ${theme.colors.blanc.bgFormField};

	/* increased hitbox */
	&::after {
		content: '';
		position: absolute;
		top: -${pxToRem(CHECKBOX_HITBOX_OFFSET_VERTICAL)};
		left: -${pxToRem(CHECKBOX_HITBOX_OFFSET_HORIZONTAL)};
		width: calc(
			${pxToRem(CHECKBOX_BOX_SIZE)} + (2 * ${pxToRem(CHECKBOX_HITBOX_OFFSET_HORIZONTAL)})
		);
		height: calc(${pxToRem(CHECKBOX_BOX_SIZE)} + (2 * ${pxToRem(CHECKBOX_HITBOX_OFFSET_VERTICAL)}));
	}

	${overrideCoreUIStyles}

	${stateStyles({
    default: {
        defaultHover: css `
				border-color: ${pickToken({
            'on-light': theme.colors.highlight.borderFormFieldHover,
            'on-medium': theme.colors.highlight.borderFormFieldHover,
            'on-dark': theme.colors.highlight.borderFormFieldOnDarkHover,
        }, $colorContext)};
				border-width: ${theme.borderWidth.m}px;
				transition: border-width ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
			`,
        defaultChecked: css `
				border: none;
				color: ${theme.colors.blanc.iconInteractiveInverse};
				background-color: ${pickToken({
            'on-light': theme.colors.highlight.bgRadiocheckboxSelected,
            'on-medium': theme.colors.highlight.bgRadiocheckboxSelected,
            'on-dark': theme.colors.highlight.bgRadiocheckboxSelectedOnDark,
        }, $colorContext)};
				transition: background-color ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
			`,
        defaultHoverChecked: css `
				background-color: ${pickToken({
            'on-light': theme.colors.highlight.bgRadiocheckboxSelectedHover,
            'on-medium': theme.colors.highlight.bgRadiocheckboxSelectedHover,
            'on-dark': theme.colors.highlight.bgRadiocheckboxSelectedHoverOnDark,
        }, $colorContext)};
			`,
    },
    error: {
        errorBase: css `
				border-color: ${pickToken({
            'on-light': theme.colors.error.borderFormField,
            'on-medium': theme.colors.error.borderFormField,
            'on-dark': theme.colors.error.borderFormFieldOnDark,
        }, $colorContext)};

				${IconContainer} {
					transition: color ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
				}
			`,
        errorHover: css `
				border-width: ${theme.borderWidth.m}px;
				transition: border-width ${theme.timing['100']}ms cubic-bezier(${theme.easings.guide});
			`,
        errorChecked: css `
				align-self: center;
				background-color: ${theme.colors.blanc.bgFormField};
				color: ${theme.colors.error.iconError};
			`,
    },
    disabled: {
        disabledBase: css `
				background-color: ${pickToken({
            'on-light': theme.colors.light.bgRadiocheckboxDisabled,
            'on-medium': theme.colors.medium.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.light.bgRadiocheckboxDisabled,
        }, $colorContext)};
				color: ${pickToken({
            'on-light': theme.colors.light.bgRadiocheckboxDisabled,
            'on-medium': theme.colors.medium.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.light.bgRadiocheckboxDisabled,
        }, $colorContext)};
				border: none;
				cursor: not-allowed;
			`,
        disabledChecked: css `
				border: none;
				cursor: not-allowed;
				background-color: ${pickToken({
            'on-light': theme.colors.light.bgRadiocheckboxDisabled,
            'on-medium': theme.colors.medium.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.light.bgRadiocheckboxDisabled,
        }, $colorContext)};
				color: ${pickToken({
            'on-light': theme.colors.medium.iconInactive,
            'on-medium': theme.colors.light.bgRadiocheckboxDisabled,
            'on-dark': theme.colors.medium.iconInactive,
        }, $colorContext)};
			`,
    },
    focus: css `
			outline: ${theme.borderWidth.m}px solid
				${pickToken({
        'on-light': theme.colors.highlight.borderFocus,
        'on-medium': theme.colors.highlight.borderFocus,
        'on-dark': theme.colors.highlight.borderFocusOnDark,
    }, $colorContext)};
			outline-offset: ${theme.borderWidth.m}px;
		`,
})}
`;
export const CheckboxBasic = styled.span `
	${checkboxBasicStyles}
`;
const checkboxSizeColorImageContainerStyles = ({ theme }) => css `
	border-radius: ${pxToRem(theme.borderRadius.s)};
	border: ${theme.borderWidth[1]}px solid transparent;
	box-sizing: border-box;

	${baseStateStyles({
    theme,
    hover: css `
			background-color: ${theme.colors.extralight.bgFormFieldHover};
		`,
    disabled: css `
			background-color: ${theme.colors.extralight.bgFormFieldDisabled};
		`,
    focus: css `
			color: ${theme.colors.action.textActive};
		`,
})}
`;
/**
 * Checkbox Size
 */
export const checkboxSizeContainerStyles = ({ theme }) => css `
	display: flex;
	justify-content: center;
	align-items: center;
	width: auto;
	height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	min-width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	padding: ${pxToRem(theme.spacing['300'])};
	text-align: center;
	background-color: ${theme.colors.extralight.bgFormField};
	color: ${theme.colors.base.textBody};

	${baseStateStyles({
    theme,
    disabled: css `
			color: ${theme.colors.medium.textInactive};
			text-decoration: line-through;
		`,
    checked: css `
			color: ${theme.colors.action.textActive};
		`,
})}
`;
export const CheckboxSizeContainer = styled(Typography).attrs({
    variant: 'button-link',
    tagName: 'span',
}) `
	${checkboxSizeColorImageContainerStyles}
	${checkboxSizeContainerStyles}
`;
const disabledGradientStyling = ({ theme }) => css `
	position: relative;

	&::after {
		position: absolute;
		content: '';
		inset: 0;
		background: linear-gradient(
			to bottom right,
			${theme.colors.base.overlay} calc(50% - 1px),
			${theme.colors.light.bgFormField} calc(50% - 1px),
			${theme.colors.light.bgFormField} 50%,
			${theme.colors.base.overlay} 50%
		);
	}
`;
const checkboxColorImageBoxStyles = ({ theme }) => css `
	border: ${theme.borderWidth[1]}px solid ${theme.colors.light.bgFormField};
	border-radius: ${pxToRem(theme.borderRadius.s)};
`;
/**
 * Checkbox Color
 */
const checkboxLabelStyled = ({ theme }) => css `
	color: ${theme.colors.base.textBody};
`;
export const CheckboxColorLabel = styled(Typography).attrs({
    variant: 'sticker-text-number-sm',
}) `
	${checkboxLabelStyled}
`;
const checkboxColorContainerStyles = ({ theme, $hasLabel, }) => css `
	background-color: ${theme.colors.extralight.bgFormField};
	text-align: center;
	display: flex;
	flex-direction: column;
	gap: ${pxToRem(theme.spacing['100'])};
	align-items: center;

	${$hasLabel
    ? `
			width: ${pxToRem(CHECKBOX_CONTENT_SIZE + Number(theme.spacing['50']) * 2)};
			padding-top: ${pxToRem(theme.spacing['50'])};
			padding-bottom: ${pxToRem(theme.spacing['100'])};
		`
    : `
			background: none;
			border-color: transparent;
		`}

	${baseStateStyles({
    theme,
    checked: css `
			${CheckboxColorLabel} {
				color: ${theme.colors.action.textActive};
			}
		`,
    disabled: css `
			${CheckboxColorLabel} {
				text-decoration: line-through;
				color: ${theme.colors.medium.textInactive};
			}
		`,
})}
`;
export const CheckboxColorContainer = styled.div `
	${checkboxSizeColorImageContainerStyles}
	${checkboxColorContainerStyles}
`;
export const checkboxColorBoxStyles = ({ $color }) => css `
	height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	${isValidImageUrl($color) ? `background-image: url(${$color});` : `background-color: ${$color};`}

	${RadioCheckboxInputContainer}[disabled] + ${CheckboxColorContainer} & {
		${disabledGradientStyling}
	}
`;
export const CheckboxColorBox = styled.span `
	${checkboxColorImageBoxStyles}
	${checkboxColorBoxStyles}
`;
const ImageSizeStyle = ({ $orientation, $height, $width, }) => {
    switch ($orientation) {
        case 'horizontal':
            return css `
				aspect-ratio: 4 / 3;

				${$height != undefined
                ? `height: ${pxToRem($height)};`
                : `width: ${pxToRem($width || CHECKBOX_HORIZONTAL_IMAGE_WIDTH)};`}
			`;
        case 'vertical':
            return css `
				aspect-ratio: 3 / 4;

				${$height != undefined
                ? `height: ${pxToRem($height)};`
                : `width: ${pxToRem($width || CHECKBOX_VERTICAL_IMAGE_WIDTH)};`}
			`;
        default:
            return `
				height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
				width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
			`;
    }
};
/**
 * Checkbox Image
 */
export const CheckboxImageContainer = styled.div `
	${checkboxSizeColorImageContainerStyles}
`;
export const checkboxImageBoxStyles = ({ theme, $src, $orientation, $height, $width, }) => css `
	background: url(${$src});
	background-size: cover;
	background-position: center;

	${ImageSizeStyle({ theme, $orientation, $height, $width })}

	${RadioCheckboxInputContainer}[disabled] + ${CheckboxImageContainer} & {
		${disabledGradientStyling}
	}
`;
export const CheckboxImageBox = styled.div `
	${checkboxColorImageBoxStyles}
	${checkboxImageBoxStyles}
`;
